import type { SnackBarConfig } from '@shared/components/Snackbar';
import type { User } from '../types/types.auth';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import Button from '@shared/components/Buttons/Button';
import { StyledLink } from '@shared/components/Link';
import { LoadingSpinner } from '@shared/components/LoadingSpinner';
import CustomSelect from '@shared/components/Select';
import CustomSnackbar from '@shared/components/Snackbar';
import { authService } from '@shared/services/AuthService';
import { clearUTMData, formatUTMDataForUser, getStoredUTMData } from '@shared/utils/utmTracking';
import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Input from '@/shared/components/Input';
import SEO from '@/shared/components/SEO';
import { UserContext } from '@/shared/contexts/UserContext';
import { SIGN_UP_FORM_FIELDS } from '../types/types.auth';
import { isEmailRegistered } from '../utils/isEmailRegistered';
import VerificationForm from './VerificationForm';

type SignUpStep = 'account' | 'verification';

interface SignUpFormProps {
  contextMessage?: string;
  handleSuccess?: (user: User) => void;
  isModal?: boolean;
  onSwitchToSignIn?: (contextMessage?: string, handleSuccess?: (user: User) => void, isModal?: boolean) => void;
  signUpFlag?: string;
}

interface SignUpFormData {
  firstName: string;
  lastName: string;
  email: string;
  company: string;
  password: string;
  referer?: string;
  inviteCode?: string;
}

const defaultForm: SignUpFormData = {
  firstName: '',
  lastName: '',
  email: '',
  company: '',
  password: '',
  inviteCode: undefined,
};

const SIGNUP_CONTENT = {
  researcher: {
    title: 'Create your free Kandir.io account',
    description:
      'Join thousands of sales professionals who use Kandir to research prospects and accelerate their sales pipeline.',
  },
  contributor: {
    title: 'Share your sales insights with Kandir',
    description:
      'Join our community of sales experts and share your experiences selling to specific accounts. Help others while building your professional network.',
  },
};

const SignUpForm: React.FC<SignUpFormProps> = ({
  contextMessage,
  handleSuccess,
  isModal,
  onSwitchToSignIn,
  signUpFlag,
}) => {
  const [step, setStep] = useState<SignUpStep>('account');
  const [formData, setFormData] = useState<SignUpFormData>(defaultForm);
  const [errors, setErrors] = useState<Partial<Record<keyof SignUpFormData, string>>>({});
  const [loading, setLoading] = useState(false);
  const [snackbarConfig, setSnackbarConfig] = useState<SnackBarConfig>();

  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { setUser, refetchUser } = useContext(UserContext);
  const [disabledFields, setDisabledFields] = useState<Set<keyof SignUpFormData>>(new Set());

  useEffect(() => {
    const email = searchParams.get('email');
    const firstName = searchParams.get('firstName');
    const lastName = searchParams.get('lastName');
    const company = searchParams.get('company');
    const referer = searchParams.get('referer');
    const inviteCode = searchParams.get('inviteCode');

    if (email || firstName || lastName || company || referer || inviteCode) {
      setFormData((prev) => ({
        ...prev,
        ...(email && { email }),
        ...(firstName && { firstName }),
        ...(lastName && { lastName }),
        ...(company && { company }),
        ...(referer && { referer }),
        ...(inviteCode && { inviteCode }),
      }));

      const fieldsToDisable = new Set<keyof SignUpFormData>();
      if (email) fieldsToDisable.add('email');
      if (firstName) fieldsToDisable.add('firstName');
      if (lastName) fieldsToDisable.add('lastName');
      if (company) fieldsToDisable.add('company');
      setDisabledFields(fieldsToDisable);
    }
  }, [searchParams]);

  const validate = () => {
    const newErrors: typeof errors = {};

    if (!formData.firstName.trim()) newErrors.firstName = 'First name is required.';
    if (!formData.lastName.trim()) newErrors.lastName = 'Last name is required.';
    if (!formData.email || !/\S[^\s@]*@\S+\.\S+/.test(formData.email)) {
      newErrors.email = 'Please enter a valid email address.';
    }
    if (!formData.company.trim()) newErrors.company = 'Company is required.';
    if (!formData.password || formData.password.length < 8) {
      newErrors.password = 'Password must be at least 8 characters.';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleInputChange = (field: keyof SignUpFormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
    if (errors[field]) setErrors((prev) => ({ ...prev, [field]: undefined }));
  };

  const handleAccountSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setLoading(true);
    try {
      const emailExists = await isEmailRegistered(formData.email);
      if (emailExists) {
        setSnackbarConfig({
          type: 'error',
          message: 'An account with this email already exists. Please sign in instead.',
          open: true,
        });
        return;
      }

      await authService.signUpUser(formData.email, formData.password);

      const skipVerification =
        searchParams.get('skipVerification') === 'true' &&
        import.meta.env.VITE_ALLOW_SKIP_EMAIL_VERIFICATION === 'true';

      if (skipVerification) {
        try {
          const confirmResponse = await axios.post(`${import.meta.env.VITE_API_BASE_URL}/auth/auto-confirm`, {
            email: formData.email,
          });
          if (confirmResponse.data?.success) {
            const storedUTMData = getStoredUTMData();
            const utmPayload = formatUTMDataForUser(storedUTMData);
            await axios.post(`${import.meta.env.VITE_API_BASE_URL}/user`, {
              firstName: formData.firstName,
              lastName: formData.lastName,
              email: formData.email,
              company: formData.company,
              positionTitle: '',
              role: 'contributor',
              referer: utmPayload.referer || formData.referer || '',
              source: utmPayload.source || '',
              campaign: utmPayload.campaign || '',
              landingPage: utmPayload.landingPage || '',
              ...(formData.inviteCode && { inviteCode: formData.inviteCode }),
            });
            const user = await authService.authenticateUser(formData.email, formData.password);
            if (!('newPasswordRequired' in user)) {
              setUser(user);
              await refetchUser({ email: user.email, idToken: user.idToken || '' });
              handleVerificationSuccess(user);
              clearUTMData();
              navigate('/onboarding');
              return;
            }
          }
        } catch {
          // fall through to verification step
        }
      }

      setStep('verification');
    } catch (error: any) {
      setSnackbarConfig({
        type: 'error',
        message: error?.message || 'Something went wrong. Please try again.',
        open: true,
      });
    } finally {
      setLoading(false);
    }
  };

  const handleVerificationSuccess = (user: User) => {
    if (handleSuccess) {
      handleSuccess(user);
    } else {
      clearUTMData();
      navigate('/onboarding');
    }
  };

  const socialLogin = (provider: 'LinkedIn' | 'Google') => {
    sessionStorage.setItem('preLoginPath', window.location.pathname);
    sessionStorage.setItem('oauthProvider', provider);
    const domain = import.meta.env.VITE_COGNITO_DOMAIN;
    const clientId = import.meta.env.VITE_COGNITO_CLIENT_ID;
    const redirectUri = import.meta.env.VITE_COGNITO_REDIRECT_URI;
    const scope = encodeURIComponent('openid email profile');
    window.location.href = `${domain}/oauth2/authorize?response_type=code&client_id=${clientId}&redirect_uri=${redirectUri}&identity_provider=${provider}&scope=${scope}`;
  };

  const handleSwitchToSignIn = () => {
    if (onSwitchToSignIn) {
      onSwitchToSignIn(contextMessage, handleSuccess, isModal);
    } else if (!isModal) {
      navigate('/sign-in');
    }
  };

  const handleSnackBarClose = () => {
    if (!snackbarConfig) return;
    setSnackbarConfig({ ...snackbarConfig, open: false });
  };

  if (step === 'verification') {
    return (
      <>
        <VerificationForm
          email={formData.email}
          password={formData.password}
          formData={formData}
          handleSuccess={handleVerificationSuccess}
          setSnackbar={setSnackbarConfig}
          setLoading={setLoading}
          signUpFlag={signUpFlag}
          isModal={isModal}
        />
        <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </>
    );
  }

  const content = signUpFlag === 'r' ? SIGNUP_CONTENT.researcher : SIGNUP_CONTENT.contributor;

  return (
    <>
      <SEO title={content.title} description={content.description} />
      <div className={`auth-form auth-form--sign-up ${isModal ? 'auth-form--modal' : ''}`}>
        {loading && (
          <div className="loader-overlay">
            <LoadingSpinner variant="circular" size="large" message="Checking details..." fullScreen />
          </div>
        )}

        <div className="form-header">
          <h1 className="form-title">Join the AE Expert Network</h1>
          <p className="form-subtitle">{contextMessage || 'Create your account to get started'}</p>
          {contextMessage && (
            <div className="auth-form__context-message">
              <span className="auth-form__context-message-icon">⚡</span>
              <span className="auth-form__context-message-text">
                You'll complete a quick onboarding, then return to complete your action
              </span>
            </div>
          )}
        </div>

        <div className="auth-form__social">
          <button
            type="button"
            onClick={() => socialLogin('LinkedIn')}
            className="button-social"
            disabled={loading}
            data-testid="linkedin-signup-button"
          >
            <img
              src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/linkedin/linkedin-original.svg"
              alt="LinkedIn"
            />
            Sign up with LinkedIn
          </button>
          <button
            type="button"
            onClick={() => socialLogin('Google')}
            className="button-social"
            disabled={loading}
            data-testid="google-signup-button"
          >
            <img src="https://cdn.jsdelivr.net/gh/devicons/devicon/icons/google/google-original.svg" alt="Google" />
            Sign up with Google
          </button>
        </div>

        <div className="divider divider--spacing-md">or sign up with</div>

        <form className="form" onSubmit={handleAccountSubmit}>
          <div className="field-group">
            <div className="field">
              <label htmlFor="firstName" className="label">
                First Name
              </label>
              <Input
                testId="sign-up-first-name"
                name="firstName"
                currentValue={formData.firstName}
                onChange={(e) => handleInputChange('firstName', e.target.value)}
                error={!!errors.firstName}
                errorText={errors.firstName}
                disabled={disabledFields.has('firstName')}
                fullWidth
              />
            </div>
            <div className="field">
              <label htmlFor="lastName" className="label">
                Last Name
              </label>
              <Input
                testId="sign-up-last-name"
                name="lastName"
                currentValue={formData.lastName}
                onChange={(e) => handleInputChange('lastName', e.target.value)}
                error={!!errors.lastName}
                errorText={errors.lastName}
                disabled={disabledFields.has('lastName')}
                fullWidth
              />
            </div>
          </div>

          <div className="field">
            <label htmlFor="email" className="label">
              Email
            </label>
            <Input
              type="email"
              testId="sign-up-email"
              name="email"
              currentValue={formData.email}
              onChange={(e) => handleInputChange('email', e.target.value)}
              error={!!errors.email}
              errorText={errors.email}
              disabled={disabledFields.has('email')}
              fullWidth
            />
          </div>

          <div className="field">
            <label htmlFor="company" className="label">
              Company
            </label>
            <Input
              testId="sign-up-company"
              name="company"
              currentValue={formData.company}
              onChange={(e) => handleInputChange('company', e.target.value)}
              error={!!errors.company}
              errorText={errors.company}
              disabled={disabledFields.has('company')}
              fullWidth
            />
          </div>

          {SIGN_UP_FORM_FIELDS.filter((f) => f.type === 'select').map((field) => (
            <div key={field.name} className="field">
              <label htmlFor={field.name} className="label">
                {field.label}
              </label>
              <CustomSelect
                name={field.name}
                testId={field.testId}
                options={field.options}
                currentValue={formData[field.name as keyof SignUpFormData] || ''}
                onChange={(e) => {
                  const formatted = Array.isArray(e) ? e.join(', ') : e;
                  handleInputChange(field.name as keyof SignUpFormData, formatted);
                }}
                fullWidth
                size="small"
                multiple={false}
              />
            </div>
          ))}

          <div className="field">
            <label htmlFor="password" className="label">
              Password
            </label>
            <Input
              type="password"
              testId="sign-up-password"
              name="password"
              currentValue={formData.password}
              onChange={(e) => handleInputChange('password', e.target.value)}
              error={!!errors.password}
              errorText={errors.password}
              fullWidth
            />
          </div>

          <div className="mt-4">
            <Button
              type="submit"
              fullWidth
              disabled={loading}
              data-testid="auth-submit-button"
              endIcon={<ArrowForwardIcon fontSize="small" />}
            >
              Continue
            </Button>
          </div>
        </form>

        <div className="link-wrapper link-wrapper--switch">
          Already have an account?
          <button type="button" onClick={handleSwitchToSignIn} className="link link--button link--regular">
            Sign in
          </button>
        </div>

        <div className="link-wrapper link-wrapper--terms">
          By creating an account, you agree to our{' '}
          <StyledLink to="https://www.kandir.io/terms" target="_blank" className="link link--small">
            Terms of Service
          </StyledLink>{' '}
          and{' '}
          <StyledLink to="https://www.kandir.io/privacy" target="_blank" className="link link--small">
            Privacy Policy
          </StyledLink>
          .
        </div>

        <CustomSnackbar config={snackbarConfig} setOpen={handleSnackBarClose} />
      </div>
    </>
  );
};

export default SignUpForm;
